<template>
  <div>
    <Head :title="`Detalles: ${provider.company_name}`" />

    <!-- Header & Actions -->
    <div class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-800 flex items-center gap-2">
          <Link href="/providers" class="text-gray-400 hover:text-orange-500 transition-colors font-bold">
            Proveedores
          </Link>
          <span class="text-gray-300 font-light">/</span>
          <span>{{ provider.company_name }}</span>
        </h1>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <Link
          :href="`/providers/${provider.id}/edit`"
          class="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm"
        >
          <i class="fa fa-pen" />
          Editar Proveedor
        </Link>
      </div>
    </div>

    <!-- Provider Info & Balance Cards -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
      <!-- General Info -->
      <div class="lg:col-span-2 bg-gradient-to-br from-teal-50 to-white p-6 rounded-2xl shadow-lg border border-teal-100 h-full">
        <h2 class="text-xl font-bold text-teal-800 mb-6 flex items-center gap-2">
          <i class="fa fa-info-circle" />
          Información General
        </h2>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="space-y-4">
            <div class="flex flex-col gap-1">
              <span class="text-xs font-bold text-gray-400 uppercase tracking-wider">Nombre del Contacto</span>
              <span class="text-gray-800 font-medium">{{ provider.name }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-xs font-bold text-gray-400 uppercase tracking-wider">Identificación / RIF</span>
              <span class="text-gray-800 font-medium">{{ provider.tax_id || '-' }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-xs font-bold text-gray-400 uppercase tracking-wider">Correo Electrónico</span>
              <a v-if="provider.email" :href="`mailto:${provider.email}`" class="text-teal-600 hover:underline font-medium">
                {{ provider.email }}
              </a>
              <span v-else class="text-gray-400 italic">No especificado</span>
            </div>
          </div>
          
          <div class="space-y-4">
            <div class="flex flex-col gap-1">
              <span class="text-xs font-bold text-gray-400 uppercase tracking-wider">Teléfono de Contacto</span>
              <span class="text-gray-800 font-medium">{{ provider.phone || '-' }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-xs font-bold text-gray-400 uppercase tracking-wider">Ubicación</span>
              <span class="text-gray-800 font-medium">
                {{ provider.address || '-' }}<br />
                <span class="text-sm text-gray-500">
                  {{ provider.city }}{{ provider.city && provider.country ? ', ' : '' }}{{ provider.country }}
                </span>
              </span>
            </div>
          </div>
        </div>
        
        <div v-if="provider.notes" class="mt-6 pt-6 border-t border-teal-100">
          <span class="text-xs font-bold text-gray-400 uppercase tracking-wider block mb-2">Notas / Comentarios</span>
          <p class="text-gray-700 text-sm leading-relaxed">{{ provider.notes }}</p>
        </div>
      </div>

      <!-- Financial Balance Card -->
      <div class="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 flex flex-col justify-between h-full">
        <div>
          <h2 class="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <i class="fa fa-wallet text-teal-600" />
            Resumen Financiero
          </h2>
          
          <div class="p-4 bg-gray-50 rounded-xl mb-6">
            <div class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Total Adeudado</div>
            <div
              :class="[
                'text-3xl font-black',
                provider.balance > 1 ? 'text-red-600' : 'text-teal-600'
              ]"
            >
              {{ formatCurrency(provider.balance) }}
            </div>
          </div>

          <div class="space-y-4 px-1">
            <div class="flex items-center justify-between py-2 border-b border-gray-50">
              <span class="text-sm text-gray-500">Facturas Pendientes</span>
              <span class="font-bold text-gray-800">{{ pendingInvoices.length }}</span>
            </div>
            <div class="flex items-center justify-between py-2">
              <span class="text-sm text-gray-500">Última Compra</span>
              <span class="font-medium text-gray-800">
                {{ lastPurchaseDate ? formatDateShort(lastPurchaseDate) : 'N/A' }}
              </span>
            </div>
          </div>
        </div>

        <div class="mt-8">
          <a
            v-if="provider.website" :href="provider.website" target="_blank"
            class="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl flex items-center justify-center gap-2 transition-colors font-bold text-sm shadow-sm"
          >
            <i class="fa fa-globe" />
            Visitar Sitio Web
          </a>
        </div>
      </div>
    </div>

    <!-- Invoices Section -->
    <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-8">
      <div class="p-6 border-b border-gray-50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 class="text-xl font-bold text-gray-800 flex items-center gap-2">
          <i class="fa fa-file-invoice text-teal-600" />
          Facturas Pendientes
        </h2>
        <span class="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-xs font-bold">
          {{ pendingInvoices.length }} pendientes de pago
        </span>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-left">
          <thead>
            <tr class="bg-gray-50/50">
              <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider">ID / N° Factura</th>
              <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider">Fecha</th>
              <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Monto</th>
              <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider">Estado</th>
              <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider text-center">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="invoice in pendingInvoices" :key="invoice.id" class="hover:bg-gray-50 transition-colors group">
              <td class="py-4 px-6">
                <Link :href="`/shoppings/${invoice.id}`" class="font-bold text-teal-700 hover:underline">
                  #{{ invoice.number_shopping || invoice.id }}
                </Link>
              </td>
              <td class="py-4 px-6 text-sm text-gray-600 font-medium">
                {{ formatDate(invoice.date) }}
              </td>
              <td class="py-4 px-6 text-right font-black text-gray-900">
                {{ formatCurrency(invoice.total) }}
              </td>
              <td class="py-4 px-6">
                <span
                  :class="[
                    'px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider',
                    invoice.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-700'
                  ]"
                >
                  {{ invoice.status === 'pending' ? 'Pendiente' : 'Pagada' }}
                </span>
              </td>
              <td class="py-4 px-6">
                <div class="flex items-center justify-center gap-3">
                  <button
                    v-if="invoice.status === 'pending'" class="h-9 px-4 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm flex items-center justify-center gap-2"
                    @click="markAsPaid(invoice.id)"
                  >
                    <i class="fa fa-check" />
                    Pagada
                  </button>
                  <a
                    :href="`/shoppings/${invoice.id}/report`" target="_blank"
                    class="w-9 h-9 flex items-center justify-center bg-white border border-gray-200 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all shadow-sm"
                    title="Ver PDF Reporte"
                  >
                    <i class="fa fa-file-pdf" />
                  </a>
                </div>
              </td>
            </tr>
            <tr v-if="pendingInvoices.length === 0">
              <td colspan="5" class="py-16 text-center">
                <div class="flex flex-col items-center justify-center gap-2 text-gray-400">
                  <i class="fa fa-check-circle text-4xl text-teal-100" />
                  <p class="italic">No hay facturas pendientes para este proveedor</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Swal from 'sweetalert2'

export default {
  name: 'ProvidersShow',

  components: {
    Head,
    Link,
  },

  layout: Layout,

  props: {
    provider: {
      type: Object,
      required: true,
    },
    shoppings: {
      type: Array,
      required: true,
    },
  },

  computed: {
    pendingInvoices() {
      return this.shoppings.filter(shopping => !shopping.paid)
    },

    lastPurchaseDate() {
      if (this.shoppings.length === 0) return null
      return this.shoppings[0].date
    },
  },

  methods: {
    back() {
      this.$inertia.visit('/providers')
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },

    formatDateShort(date) {
      return new Date(date).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('es-US', {
        style: 'currency',
        currency: 'USD',
      }).format(value || 0)
    },

    async markAsPaid(invoiceId) {
      const result = await Swal.fire({
        title: '¿Confirmar pago?',
        text: '¿Estás seguro de que quieres marcar esta factura como pagada?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#0d9488', // teal-600
        cancelButtonColor: '#9ca3af',
        confirmButtonText: 'Sí, confirmar',
        cancelButtonText: 'Cancelar',
      })

      if (result.isConfirmed) {
        try {
          this.$inertia.get(`/shoppings/${invoiceId}/paid`, {}, {
            onSuccess: () => {
              Swal.fire({
                title: '¡Éxito!',
                text: 'La factura ha sido marcada como pagada correctamente.',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false,
              })
            },
          })
        } catch (error) {
          Swal.fire('Error', 'Hubo un problema al procesar el pago.', 'error')
        }
      }
    },
  },
}
</script>
